import React, { useState } from 'react';
import Modal from '../common/Modal';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';

const MIN_AGE_YEARS = 18;

function isAgeAtLeast18(dobStr: string): boolean {
  const dob = new Date(dobStr);
  const today = new Date();
  const threshold = new Date(today.getFullYear() - MIN_AGE_YEARS, today.getMonth(), today.getDate());
  return dob <= threshold;
}

function maxDobDate(): string {
  const today = new Date();
  const max = new Date(today.getFullYear() - MIN_AGE_YEARS, today.getMonth(), today.getDate());
  return max.toISOString().split('T')[0];
}

interface AgeVerificationModalProps {
  isOpen: boolean;
}

export const AgeVerificationModal: React.FC<AgeVerificationModalProps> = ({ isOpen }) => {
  const [dob, setDob] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [ageError, setAgeError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setDobClaim } = useAuth();
  const setUser = useAuthStore(s => s.setUser);
  const user = useAuthStore(s => s.user);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dob) {
      setAgeError('Please enter your date of birth.');
      return;
    }
    if (!isAgeAtLeast18(dob)) {
      setAgeError('You must be 18 or older to play HexCrawl.');
      return;
    }
    if (!tosAccepted) {
      setAgeError('You must accept the Privacy Policy to continue.');
      return;
    }
    setAgeError(null);
    setIsSubmitting(true);
    try {
      await setDobClaim(new Date(dob));
      // Update local user state so the modal closes
      if (user) {
        setUser({ ...user, hasAgeVerification: true });
      }
    } catch {
      setAgeError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} className="login-modal">
      <div className="text-white">
        <h2 style={{ textAlign: 'center' }} className="text-2xl font-bold mb-4">One More Step</h2>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ fontSize: '14px', color: '#ccc', textAlign: 'center' }}>
            HexCrawl uses AI-generated content. Please confirm you are 18 or older to continue.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label htmlFor="age-verify-dob" style={{ fontSize: '13px', color: '#aaa' }}>Date of Birth</label>
            <input
              id="age-verify-dob"
              type="date"
              value={dob}
              max={maxDobDate()}
              onChange={e => setDob(e.target.value)}
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #555',
                background: '#222',
                color: 'white',
                fontSize: '14px',
                colorScheme: 'dark',
              }}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#ccc', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={tosAccepted}
              onChange={e => setTosAccepted(e.target.checked)}
              style={{ marginTop: '2px', flexShrink: 0 }}
            />
            I confirm I am 18 or older and agree to the{' '}
            <a href="https://hexcrawl.online/privacy" target="_blank" rel="noreferrer" style={{ color: '#7289DA' }}>
              Privacy Policy
            </a>
            .
          </label>

          {ageError && (
            <p style={{ color: '#ff6b6b', fontSize: '13px', margin: 0 }}>{ageError}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '10px 16px',
              background: '#7289DA',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {isSubmitting ? 'Verifying...' : 'Continue'}
          </button>
        </form>
      </div>
    </Modal>
  );
};
